<template>
  <div
    class="garage-vehicle-details"
    :class="`garage-vehicle-details--${template}`"
  >
    <component
      :is="interactive ? 'button' : 'div'"
      class="garage-vehicle-details__container"
      :class="{
        'garage-vehicle-details__container--button': interactive,
        'garage-vehicle-details__container--centre': !formattedVehicle.strings?.text,
      }"
      :type="interactive ? 'button' : null"
      @click="interactive ? handleSetActiveVehicle() : null"
    >
      <garage-icon :type="vehicle.details?.type" />

      <div
        v-if="vehicle"
        class="garage-vehicle-details__content"
      >
        <p
          v-if="formattedVehicle.strings?.title"
          class="garage-vehicle-details__title"
          :class="textClasses.title"
          v-text="formattedVehicle.strings.title"
        />

        <p
          v-if="formattedVehicle.strings?.text"
          class="garage-vehicle-details__text"
          :class="textClasses.text"
          v-text="formattedVehicle.strings.text"
        />

        <btn
          v-if="template === 'account' && !active"
          class="garage-vehicle-details__make-default"
          class-label="text-body-xs-underline"
          modifiers="text"
          :label="$string('customers.garage.make_default_vehicle')"
          type="button"
          @click="handleSetActiveVehicle"
        />
      </div>

      <div
        v-else
        class="garage-vehicle-details__content"
      >
        <p
          class="text-body-s-regular text-body-m-regular-desktop"
          v-text="$string('garage.banner.add_vehicle')"
        />
      </div>
    </component>

    <button
      v-if="showRemove"
      class="garage-vehicle-details__remove icon-button"
      type="button"
      @click="handleRemove"
    >
      <span
        class="visually-hidden"
        v-text="$string('garage.cta_labels.remove')"
      />

      <icon-bin aria-hidden="true" />
    </button>
  </div>
</template>

<script>

/**
 * Vue: Garage vehicle details (garage-vehicle-details)
 * -----------------------------------------------------------------------------
 * Sub-component to display active vehicles details.
 *
 * @param {Boolean} [active] - Active vehicle?
 * @param {String} template - Component rendering template, either `banner`,
 * `hero-modal`, `hero-results`, `modal`, `product`, or `account`.
 * @param {Boolean|import("~stores/garage").Vehicle} vehicle - Vehicle.
 *
 * @emits removeVehicle - Emit remove vehicle event.
 * @emits setActiveVehicle - Emit set active vehicle event.
 *
 */
import { mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import GarageIcon from '~async/garage-icon/garage-icon'

import IconBin from '~icons/general/bin.svg'

import { validateVehicle } from '~/helpers/garage'

export default {
  name: 'GarageVehicleDetails',

  components: {
    Btn,
    GarageIcon,
    IconBin,
  },

  props: {
    active: {
      type: Boolean,
      default: false,
    },
    template: {
      type: String,
      default: 'banner',
      required: true,
      validator: (value) => ['account', 'banner', 'hero-modal', 'hero-results', 'missing', 'modal', 'product'].includes(value),
    },
    vehicle: {
      type: [Boolean, Object],
      default: false,
      required: true,
      validator: validateVehicle,
    },
  },

  emits: {
    'remove-vehicle': (payload) => typeof payload === 'object',
    'set-active-vehicle': (payload) => typeof payload === 'string',
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFormattedVehicle: 'garage/getFormattedVehicle',
    }),

    /**
     * Compute formatted vehicle.
     * @returns {import("~stores/garage").FormattedVehicle}
     */
    formattedVehicle() {
      return this.getFormattedVehicle(this.vehicle)
    },

    /**
     * Compute if element should be interactive.
     * @returns {Boolean}
     */
    interactive() {
      return this.vehicle && this.template === 'modal'
    },

    /**
     * Compute if remove button should be shown.
     * @returns {Boolean}
     */
    showRemove() {
      return this.vehicle && ['account', 'modal'].includes(this.template)
    },

    /**
     * Compute text classes.
     * @returns {Object}
     */
    textClasses() {
      switch (this.template) {
        case 'account':
          return {
            title: 'text-heading-m',
            text: 'text-body-s-regular',
          }

        case 'hero-modal':
          return {
            title: 'text-body-s-bold',
            text: 'text-body-xs-regular text-body-2xs-regular-desktop',
          }

        case 'hero-results':
          return {
            title: 'text-body-s-bold text-heading-xl-desktop',
            text: 'text-body-xs-regular text-body-m-regular-desktop',
          }

        case 'missing':
          return {
            title: 'text-body-s-bold',
            text: 'text-body-xs-regular',
          }

        case 'modal':
          return {
            title: 'text-body-xs-bold',
            text: 'text-body-2xs-regular',
          }

        case 'product':
          return {
            title: 'text-body-xs-bold',
            text: 'text-body-xs-regular',
          }

        default:
          return {
            title: 'text-body-s-bold',
            text: 'text-body-xs-regular',
          }
      }
    },
  },

  methods: {

    /**
     * Handle set active vehicle.
     */
    handleSetActiveVehicle() {
      this.$emit('set-active-vehicle', this.vehicle.id)
    },

    /**
     * Handle remove.
     */
    handleRemove() {
      this.$emit('remove-vehicle', this.vehicle)
    },
  },
}
</script>

<style lang="scss">
@import './garage-vehicle-details';
</style>
